// src/pages/components/AnalyticsTab.jsx
import React, { useEffect, useMemo, useState } from "react";

/** ======== CONFIG ======== */
const API_BASE = "https://oppiesfunc.azurewebsites.net/api";
const KEYS = {
    getShopViewsByDay: "GZA-D0kLaQFBiXiOsniLD_E6CObb5amUX2SVrfdHjq9TAzFuJVj6tA==",
    listReviewsByShop: "sAceAu7299X416KnMS_uczKnhi_om2aA8upvrDjpkS2hAzFug9NXlg==",
};

/** ======== HELPERS ======== */
const apiGet = async (path) => {
    const res = await fetch(`${API_BASE}${path}`, {
        method: "GET",
        headers: { "Cache-Control": "no-store" },
    });
    if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(`${res.status} ${res.statusText} — ${txt}`);
    }
    return res.json();
};

const safeDate = (v) => {
    if (!v) return null;
    // Accept "YYYY-MM-DD", ISO strings, numeric timestamp, or objects with y/m/d
    if (typeof v === "object" && v.year && v.month && v.day) {
        const d = new Date(v.year, (v.month || 1) - 1, v.day);
        return isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
};

const ymd = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};

const fmtDay = (d) =>
    d?.toLocaleDateString("en-NZ", { day: "2-digit", month: "short" }) || "";

const fmtMonth = (d) =>
    d?.toLocaleDateString("en-NZ", { month: "short", year: "numeric" }) || "";

const sum = (arr) => arr.reduce((a, b) => a + (Number(b) || 0), 0);
const avg = (arr) => (arr.length ? sum(arr) / arr.length : 0);

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

/** Build last N days array of dates (midnight local) */
const lastNDays = (n) => {
    const out = [];
    const base = new Date();
    base.setHours(0, 0, 0, 0);
    for (let i = n - 1; i >= 0; i--) {
        const d = new Date(base);
        d.setDate(base.getDate() - i);
        out.push(d);
    }
    return out;
};

/** Normalize views response into continuous last-30-day series */
const normalizeViewsSeries = (vres, days = 30) => {
    // Accept shapes: {days:[{date,count}]}, {items:[...]}, {grouped:[...]}, or bare array
    const raw =
        (Array.isArray(vres?.days) && vres.days) ||
        (Array.isArray(vres?.items) && vres.items) ||
        (Array.isArray(vres?.grouped) && vres.grouped) ||
        (Array.isArray(vres) && vres) ||
        [];

    // Normalize records to {dateStr:'YYYY-MM-DD', count:number}
    const rows = raw
        .map((r) => {
            const dRaw =
                r.date ||
                r.day ||
                r.dayString ||
                r._id ||
                r.createdAt ||
                r.timestamp ||
                r.key ||
                r.partitionKey ||
                (r.year && r.month && r.day ? { year: r.year, month: r.month, day: r.day } : null);
            const d = safeDate(dRaw);
            if (!d) return null;
            const dateStr = ymd(d);
            const count = Number(
                r.count ?? r.views ?? r.total ?? r.value ?? r.cnt ?? 0
            );
            return { dateStr, count: isNaN(count) ? 0 : count };
        })
        .filter(Boolean);

    // Aggregate in case there are multiple rows per day
    const map = Object.create(null);
    for (const row of rows) {
        map[row.dateStr] = (map[row.dateStr] || 0) + row.count;
    }

    // Fill missing days
    const daysArr = lastNDays(days);
    return daysArr.map((d) => ({ x: d, y: map[ymd(d)] || 0 }));
};

/** Aggregate monthly average rating series from review list */
const toMonthlyRatingTrend = (reviews) => {
    const buckets = new Map(); // "YYYY-MM" -> [ratings...]
    for (const r of reviews) {
        if (!r.createdAt || !r.rating) continue;
        const key = `${r.createdAt.getFullYear()}-${String(
            r.createdAt.getMonth() + 1
        ).padStart(2, "0")}`;
        const arr = buckets.get(key) || [];
        arr.push(r.rating);
        buckets.set(key, arr);
    }
    return Array.from(buckets.entries())
        .sort(([a], [b]) => (a < b ? -1 : 1))
        .map(([ym, arr]) => {
            const [y, m] = ym.split("-").map(Number);
            return { x: new Date(y, m - 1, 1), y: avg(arr) };
        });
};

/** ======== MINI CHARTS (inline SVG) ======== */
function MiniBarChart({ data, height = 120, labelFormatter = fmtDay }) {
    // data: [{ x: Date, y: number }, ...]
    const width = Math.max(280, data.length * 18);
    const maxY = Math.max(...data.map((d) => d.y), 1);
    const barWidth = Math.floor(width / data.length) - 4;

    return (
        <div style={{ overflowX: "auto" }}>
            <svg width={width} height={height}>
                {data.map((d, i) => {
                    const h = Math.round((d.y / maxY) * (height - 28));
                    const x = i * (barWidth + 4) + 2;
                    const y = height - 20 - h;
                    return (
                        <g key={i}>
                            <rect
                                x={x}
                                y={y}
                                width={barWidth}
                                height={h}
                                fill="#bfa97c"
                                stroke="#8d7a58"
                                strokeWidth="0.5"
                                rx="3"
                            />
                            {/* x labels (sparse) */}
                            {i % Math.ceil(data.length / 7 || 1) === 0 && (
                                <text
                                    x={x + barWidth / 2}
                                    y={height - 6}
                                    fontSize="10"
                                    fill="#5c4a3b"
                                    textAnchor="middle"
                                >
                                    {labelFormatter(d.x)}
                                </text>
                            )}
                        </g>
                    );
                })}
                {/* y-axis guide */}
                <line
                    x1="0"
                    y1={height - 20}
                    x2={width}
                    y2={height - 20}
                    stroke="#eadfc9"
                />
            </svg>
        </div>
    );
}

function MiniLineChart({ data, height = 120 }) {
    // data: [{ x: Date, y: number }, ...] (assumed sorted)
    const width = Math.max(280, data.length * 50);
    const maxY = Math.max(...data.map((d) => d.y), 1);
    const minY = Math.min(...data.map((d) => d.y), 0);

    const padTop = 8;
    const padBottom = 28;
    const usableH = height - padTop - padBottom;

    const points = data.map((d, i) => {
        const x = (i / Math.max(1, data.length - 1)) * (width - 20) + 10;
        const y =
            padTop +
            usableH -
            ((d.y - minY) / Math.max(1, maxY - minY)) * usableH;
        return { x, y };
    });

    const path = points
        .map((p, i) => (i === 0 ? `M ${p.x},${p.y}` : `L ${p.x},${p.y}`))
        .join(" ");

    return (
        <div style={{ overflowX: "auto" }}>
            <svg width={width} height={height}>
                {/* baseline */}
                <line
                    x1="0"
                    y1={height - padBottom}
                    x2={width}
                    y2={height - padBottom}
                    stroke="#eadfc9"
                />
                {/* line */}
                <path d={path} fill="none" stroke="#2e7d32" strokeWidth="2" />
                {/* points + labels */}
                {points.map((p, i) => (
                    <g key={i}>
                        <circle cx={p.x} cy={p.y} r="3" fill="#2e7d32" />
                        <text
                            x={p.x}
                            y={height - 10}
                            fontSize="10"
                            textAnchor="middle"
                            fill="#5c4a3b"
                        >
                            {fmtMonth(data[i].x)}
                        </text>
                    </g>
                ))}
            </svg>
        </div>
    );
}

/** ======== MAIN COMPONENT ======== */
export default function AnalyticsTab() {
    const shopId = useMemo(() => localStorage.getItem("shopId") || "", []);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    // Views (daily)
    const [viewsSeries, setViewsSeries] = useState([]); // [{x:Date,y:number}]
    const [viewsTotal30, setViewsTotal30] = useState(0);

    // Reviews
    const [avgRating, setAvgRating] = useState(0);
    const [totalReviews, setTotalReviews] = useState(0);
    const [newReviews30, setNewReviews30] = useState(0);
    // idx 0→1★, 4→5★
    const [ratingDist, setRatingDist] = useState([0, 0, 0, 0, 0]);
    const [reviewTrend, setReviewTrend] = useState([]); // [{x:Date,y:number}] avg per month

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            if (!shopId) {
                setLoading(false);
                return;
            }
            setErr("");
            setLoading(true);

            try {
                /** -------- Views by day -------- */
                const vres = await apiGet(
                    `/getShopViewsByDay?code=${KEYS.getShopViewsByDay}&shopId=${encodeURIComponent(
                        shopId
                    )}&_t=${Date.now()}`
                );
                const vseries = normalizeViewsSeries(vres, 30);
                const views30 = sum(vseries.map((p) => p.y));

                if (!cancelled) {
                    setViewsSeries(vseries);
                    setViewsTotal30(views30);
                }

                /** -------- Reviews by shop -------- */
                const rres = await apiGet(
                    `/listReviewsByShop?code=${KEYS.listReviewsByShop}&shopId=${encodeURIComponent(
                        shopId
                    )}&_t=${Date.now()}`
                );
                const raw = Array.isArray(rres?.items)
                    ? rres.items
                    : Array.isArray(rres?.reviews)
                        ? rres.reviews
                        : Array.isArray(rres)
                            ? rres
                            : [];

                const reviews = raw
                    .map((r) => ({
                        rating: Number(r.rating ?? r.stars ?? r.score ?? 0),
                        createdAt: safeDate(r.createdAt ?? r.timestamp ?? r.date ?? r._ts) || null,
                    }))
                    .filter((r) => Number.isFinite(r.rating) && r.rating >= 1 && r.rating <= 5);

                const ratingsOnly = reviews.map((r) => r.rating);
                const avgR = ratingsOnly.length ? avg(ratingsOnly) : 0;

                // Correct bucket: index = (rating - 1)
                const dist = [0, 0, 0, 0, 0];
                for (const n of ratingsOnly) {
                    const rounded = Math.round(n); // if ratings are decimals
                    const star = clamp(rounded, 1, 5);
                    dist[star - 1] += 1;
                }

                const now = Date.now();
                const new30 = reviews.filter(
                    (r) => r.createdAt && now - r.createdAt.getTime() <= 30 * 24 * 3600 * 1000
                ).length;

                const trend = toMonthlyRatingTrend(reviews);

                if (!cancelled) {
                    setAvgRating(avgR);
                    setTotalReviews(reviews.length);
                    setNewReviews30(new30);
                    setRatingDist(dist);
                    setReviewTrend(trend);
                }
            } catch (e) {
                if (!cancelled) setErr(e.message || "Failed to load analytics.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [shopId]);

    /** ======== UI ======== */
    return (
        <div style={styles.page}>
            <div style={styles.header}>Analytics</div>

            {err && <div style={styles.error}>{err}</div>}

            {/* KPI tiles */}
            <div style={styles.kpis}>
                <div style={styles.kpi}>
                    <div style={styles.kpiLabel}>Views (last 30 days)</div>
                    <div style={styles.kpiValue}>{viewsTotal30}</div>
                </div>
                <div style={styles.kpi}>
                    <div style={styles.kpiLabel}>Average Rating</div>
                    <div style={styles.kpiValue}>
                        {avgRating ? avgRating.toFixed(2) : "—"} <span style={styles.kpiUnit}>/ 5</span>
                    </div>
                </div>
                <div style={styles.kpi}>
                    <div style={styles.kpiLabel}>Total Reviews</div>
                    <div style={styles.kpiValue}>{totalReviews}</div>
                </div>
                <div style={styles.kpi}>
                    <div style={styles.kpiLabel}>New Reviews (30 days)</div>
                    <div style={styles.kpiValue}>{newReviews30}</div>
                </div>
            </div>

            {/* Views chart */}
            <div style={styles.card}>
                <div style={styles.cardTitle}>Shop Profile Views (by day)</div>
                {loading && viewsSeries.length === 0 ? (
                    <div style={styles.dim}>Loading…</div>
                ) : viewsSeries.length === 0 ? (
                    <div style={styles.dim}>No view data yet.</div>
                ) : (
                    <MiniBarChart data={viewsSeries} labelFormatter={(d) => fmtDay(d)} />
                )}
            </div>

            {/* Reviews trend + distribution */}
            <div style={styles.grid2}>
                <div style={styles.card}>
                    <div style={styles.cardTitle}>Average Rating Trend (by month)</div>
                    {loading && reviewTrend.length === 0 ? (
                        <div style={styles.dim}>Loading…</div>
                    ) : reviewTrend.length === 0 ? (
                        <div style={styles.dim}>No review data yet.</div>
                    ) : (
                        <MiniLineChart data={reviewTrend} />
                    )}
                </div>

                <div style={styles.card}>
                    <div style={styles.cardTitle}>Rating Distribution</div>
                    {totalReviews === 0 ? (
                        <div style={styles.dim}>No reviews yet.</div>
                    ) : (
                        <div style={{ display: "grid", gap: 6 }}>
                            {[5, 4, 3, 2, 1].map((stars) => {
                                const idx = stars - 1; // 1★->0 ... 5★->4
                                const count = ratingDist[idx] || 0;
                                const pct =
                                    totalReviews > 0 ? Math.round((count / totalReviews) * 100) : 0;
                                return (
                                    <div key={stars} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        <div style={{ width: 40, textAlign: "right", color: "#5c4a3b" }}>
                                            {stars}★
                                        </div>
                                        <div style={styles.distBarWrap}>
                                            <div
                                                style={{ ...styles.distBarFill, width: `${pct}%` }}
                                                title={`${count} review${count === 1 ? "" : "s"}`}
                                            />
                                        </div>
                                        <div style={{ width: 42, textAlign: "left", color: "#5c4a3b" }}>
                                            {count}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

/** ======== STYLES ======== */
const styles = {
    page: {
        fontFamily: "Calibri, sans-serif",
        color: "#333",
    },
    header: {
        fontWeight: "bold",
        fontSize: 18,
        color: "#5c4a3b",
        marginBottom: 12,
    },
    error: {
        background: "#fff0f0",
        border: "1px solid #e6b8b8",
        color: "#8a1f1f",
        padding: "10px 12px",
        borderRadius: 8,
        marginBottom: 12,
    },

    kpis: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        gap: 12,
        marginBottom: 16,
    },
    kpi: {
        background: "#fffef9",
        border: "1px solid #eadfc9",
        borderRadius: 10,
        padding: "12px 14px",
    },
    kpiLabel: { fontSize: 12, color: "#6b5a47", marginBottom: 4 },
    kpiValue: { fontSize: 22, fontWeight: "bold", color: "#5c4a3b" },
    kpiUnit: { fontSize: 14, color: "#6b5a47", fontWeight: "normal" },

    card: {
        background: "#fffef9",
        border: "1px solid #eadfc9",
        borderRadius: 10,
        padding: 12,
        marginBottom: 14,
    },
    cardTitle: {
        fontWeight: "bold",
        color: "#5c4a3b",
        marginBottom: 8,
    },
    dim: { color: "#777", textAlign: "center", padding: 16 },

    grid2: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 12,
    },

    distBarWrap: {
        flex: 1,
        background: "#fff9ec",
        border: "1px solid #eadfc9",
        borderRadius: 8,
        height: 12,
        overflow: "hidden",
    },
    distBarFill: {
        height: "100%",
        background: "#bfa97c",
    },
};
